import { createContext, useCallback, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';

import { useToast } from '@contexts/Toast.context';
import {
	createVideoProject,
	uploadVideoToS3,
	updateVideoUploadMetadata,
	startVideoProcessing,
} from '@utils/api/videoProjects';
import { archiveProject } from '@utils/api/projects';
import { getUserFriendlyError } from '@utils/api/errorMessages';
import {
	DEFAULT_MODEL_OPTION,
	countSubject,
	modelForSubject,
} from '@config/videoDetectionModelConfig';
import { withReportSafeName } from '@utils/video/reportText';

/** Detection confidence the job starts from (BFM reindeer; docs/REINDEER_VIDEO_COUNTING.md). The results page can
 * raise it afterwards in the browser, never lower it, so the wizard's value is the floor. */
export const DEFAULT_CONFIDENCE = 0.3;

// what a failed start of the analysis says in its toast: a sentence, never the transport's "Not Found"
const START_FAILED = 'The analysis could not be started. Try again in a moment.';
const START_ERRORS = { 404: START_FAILED, 500: START_FAILED, 502: START_FAILED, 503: START_FAILED };

const VideoProjectCreationContext = createContext(null);

export const VideoProjectCreationProvider = ({ children, projectInfo }) => {
	const navigate = useNavigate();
	const { addToast } = useToast();

	// Wizard state
	const [currentStage, setCurrentStage] = useState(3);

	// Video file state (local). A name the PDF report cannot write is changed on selection; the
	// setter stays stable like a useState setter, as the drop zones' callbacks depend on it.
	const [videoFile, setVideoFileState] = useState(null);
	const setVideoFile = useCallback(
		file => setVideoFileState(file ? withReportSafeName(file) : null),
		[]
	);
	const [videoURL, setVideoURL] = useState(null);
	const videoRef = useRef(null);

	// Metadata
	const [videoMetadata, setVideoMetadata] = useState(null);

	// Configuration
	const [configuration, setConfiguration] = useState({
		countingScenario: null,
		countingMethod: null,
		modelOption: DEFAULT_MODEL_OPTION,
		virtualLineDefinition: null,
		timeRange: null,
		confidenceThreshold: DEFAULT_CONFIDENCE,
	});

	// Upload state
	const [projectUuid, setProjectUuid] = useState(null);
	const [videoUuid, setVideoUuid] = useState(null);
	const [jobUuid, setJobUuid] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState(null);

	// Abort controller for future cancellation support
	const uploadAbortControllerRef = useRef(null);

	// Create blob URL for local preview
	useEffect(() => {
		if (videoFile) {
			const url = URL.createObjectURL(videoFile);
			setVideoURL(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setVideoURL(null);
			// Reset metadata and configuration when video file is removed
			setVideoMetadata(null);
			setConfiguration({
				countingScenario: null,
				countingMethod: null,
				modelOption: DEFAULT_MODEL_OPTION,
				virtualLineDefinition: null,
				timeRange: null,
				confidenceThreshold: DEFAULT_CONFIDENCE,
			});
		}
	}, [videoFile]);

	// Navigation
	const nextStage = () => setCurrentStage(prev => Math.min(prev + 1, 6));
	const previousStage = () => setCurrentStage(prev => Math.max(prev - 1, 3));
	const goToStage = stage => setCurrentStage(Math.min(6, Math.max(3, stage)));

	// Can proceed validation
	const canProceedFromStage = stage => {
		switch (stage) {
			case 3:
				return !!videoFile;

			case 4: {
				const hasScenario = !!configuration.countingScenario;
				const hasMethod = !!configuration.countingMethod;

				// Time range is optional if video can't be previewed
				const hasTimeRange = !!configuration.timeRange || !videoMetadata?.duration;

				// Virtual line only required if method is line_based AND video can be previewed
				const hasVirtualLine =
					configuration.countingMethod !== 'line_based' ||
					!videoMetadata?.duration ||
					!!configuration.virtualLineDefinition;

				return hasScenario && hasMethod && hasTimeRange && hasVirtualLine;
			}
			case 5:
				return !!projectUuid && uploadProgress === 100;

			case 6:
				return true;

			default:
				return false;
		}
	};

	// Mutations
	const { mutateAsync: createProjectMutation } = useMutation({
		mutationFn: createVideoProject,
		onMutate: () => console.log('%c 🛠️ Creating Video Project', 'color: #198754'),
		onError: err => console.error(`Error creating video project: ${err}`),
	});

	const { mutateAsync: uploadVideoMutation } = useMutation({
		mutationFn: uploadVideoToS3,
		onMutate: () => console.group('%c☁️ Uploading video', 'color: #0d6efd'),
		onError: err => console.error(`Error uploading video: ${err}`),
		onSettled: () => console.groupEnd(),
	});

	const { mutateAsync: updateVideoMetadataMutation } = useMutation({
		mutationFn: updateVideoUploadMetadata,
		onMutate: () => console.log('%c📝 Updating video metadata', 'color: #FFC107'),
		onError: err => console.error(`Error updating video metadata: ${err}`),
	});

	const { mutateAsync: startProcessingMutation } = useMutation({
		mutationFn: startVideoProcessing,
		onMutate: () => console.log('%c⚙️ Starting video processing', 'color: #D49DFF'),
		onError: err => console.error(`Error starting video processing: ${err}`),
		onSuccess: () => console.log('%c✅ Video processing started', 'color: #00d26a'),
	});

	const { mutateAsync: archiveProjectMutation } = useMutation({
		mutationFn: archiveProject,
		onMutate: () => console.log('%c📦 Archiving project due to error', 'color: #FF5722'),
		onError: err => console.error(`Error archiving project: ${err}`),
	});

	// Helper function to build processing payload. The wizard gives the subject it shows, which can be the AI's
	// suggestion; without one the payload uses the typed word.
	const buildProcessingPayload = (shownSubject = configuration.subject) => {
		// what to count decides the model: our reindeer model, or the general search for anything else
		const subject = countSubject(shownSubject);
		const selectedModel = modelForSubject(subject);

		const payload = {
			counting_scenario: configuration.countingScenario,
			model_id: selectedModel.value,
			model_version: selectedModel.modelVersion,
			text_prompt: subject,
		};

		if (configuration.countingMethod) {
			payload.counting_method = configuration.countingMethod;
		}
		if (configuration.virtualLineDefinition) {
			payload.virtual_line = configuration.virtualLineDefinition;
		}
		if (Number.isFinite(configuration.confidenceThreshold)) {
			payload.confidence_threshold = configuration.confidenceThreshold;
		}
		if (configuration.timeRange) {
			payload.analysis_start_seconds = configuration.timeRange.start_seconds;
			payload.analysis_end_seconds = configuration.timeRange.end_seconds;
		}

		return payload;
	};

	// Shared processing function
	const startProcessing = async (pUuid = projectUuid, vUuid = videoUuid, subject) => {
		if (!pUuid || !vUuid) {
			throw new Error('Project UUID and Video UUID are required');
		}

		const processingPayload = buildProcessingPayload(subject);

		const processingResponse = await startProcessingMutation({
			projectUuid: pUuid,
			videoUuid: vUuid,
			configuration: processingPayload,
		});

		setJobUuid(processingResponse.data.job_uuid);
	};

	// Upload handler
	const createProjectAndUpload = async ({ subject } = {}) => {
		let createdProjectId = null;
		try {
			setIsUploading(true);
			setError(null);

			// Create abort controller for future cancellation support
			uploadAbortControllerRef.current = new AbortController();

			// 1. Create project
			const payload = {
				title: projectInfo.title?.normalize('NFC'),
				videos: [{ video_filename: videoFile.name, file_size: videoFile.size }],
			};

			if (projectInfo.description) payload.description = projectInfo.description;
			if (projectInfo.tags?.length > 0) payload.tags = projectInfo.tags;

			const response = await createProjectMutation(payload);
			const createdProjectUuid = response.data.uuid;
			createdProjectId = createdProjectUuid;
			const createdVideoUuid = response.data.videos[0].uuid;

			setProjectUuid(createdProjectUuid);
			setVideoUuid(createdVideoUuid);

			// 2. Upload video
			const uploadData = await uploadVideoMutation({
				file: videoFile,
				projectId: createdProjectUuid,
				signal: uploadAbortControllerRef.current.signal, // TODO: add support in Upload class
				onProgress: setUploadProgress,
			});

			// 3 Update video metadata with uploadId and key
			await updateVideoMetadataMutation({
				projectUuid: createdProjectUuid,
				videoUuid: createdVideoUuid,
				uploadId: uploadData.uploadId,
				key: uploadData.key,
			});

			// 4. Start processing
			let processingSuccess = false;
			try {
				setIsProcessing(true);
				await startProcessing(createdProjectUuid, createdVideoUuid, subject);
				processingSuccess = true;
			} catch (processingErr) {
				console.error('Processing failed to start:', processingErr);
				addToast({
					id: 'video-processing-start-error',
					title: 'Processing failed to start',
					message: getUserFriendlyError(processingErr, START_ERRORS),
					bg: 'warning',
				});
			} finally {
				setIsProcessing(false);
			}

			// 5. If processing succeeded, open the project page (it shows the analysis while it runs).
			//    If failed, show finalize stage for retry.
			if (processingSuccess) {
				navigate(`/video-projects/${createdProjectUuid}`, { replace: true });
			} else {
				nextStage();
			}
		} catch (caught) {
			// the multipart Upload rejects with undefined when the file service fails: keep an error to show
			const err = caught ?? new Error('The upload did not finish');
			if (err.name === 'AbortError') {
				console.log('Upload cancelled by user');
			} else {
				console.error('Error in project creation:', err);
				setError(err);

				addToast({
					id: 'video-project-creation-error',
					title: 'Project creation failed',
					message: getUserFriendlyError(err),
					bg: 'danger',
				});
			}

			try {
				// Archive project if it was created
				if (createdProjectId) {
					await archiveProjectMutation(createdProjectId);
				}
			} catch (err) {
				console.warn('Project archive failed: ', err);
			} finally {
				// Always reset so the UI can recover
				setUploadProgress(0);
			}
		} finally {
			setIsUploading(false);
			uploadAbortControllerRef.current = null;
		}
	};

	// Cancel upload (ready for future use)
	const cancelUpload = () => {
		uploadAbortControllerRef.current?.abort();
	};

	// Retry processing
	const retryProcessing = async ({ subject } = {}) => {
		try {
			setIsProcessing(true);
			await startProcessing(projectUuid, videoUuid, subject);

			addToast({
				id: 'video-processing-retry-success',
				title: 'Processing started',
				message: 'Video processing has been started successfully.',
				bg: 'success',
			});
			return true;
		} catch (err) {
			console.error('Retry processing failed:', err);
			addToast({
				id: 'video-processing-retry-error',
				title: 'Retry failed',
				message: getUserFriendlyError(err, START_ERRORS),
				bg: 'danger',
			});
			return false;
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<VideoProjectCreationContext.Provider
			value={{
				// Wizard
				currentStage,
				nextStage,
				previousStage,
				goToStage,
				canProceedFromStage,

				// Video
				videoFile,
				setVideoFile,
				videoURL,
				videoRef,

				// Metadata
				videoMetadata,
				setVideoMetadata,

				// Configuration
				configuration,
				setConfiguration,

				// Upload
				projectUuid,
				videoUuid,
				jobUuid,
				uploadProgress,
				isUploading,
				isProcessing,
				error,
				createProjectAndUpload,
				cancelUpload,
				retryProcessing,
			}}>
			{children}
		</VideoProjectCreationContext.Provider>
	);
};

export const useVideoProjectCreation = () => {
	const context = useContext(VideoProjectCreationContext);
	if (!context) {
		throw new Error('useVideoProjectCreation must be used within VideoProjectCreationProvider');
	}
	return context;
};
